"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ServiceQuoteSection from "@/components/service/ServiceQuoteSection";
import ServiceFormSuccess from "@/components/service/ServiceFormSuccess";
import { inputClass, textareaClass, selectClass, labelClass, submitClass, formCardClass } from "@/components/service/formStyles";
function TurfingQuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <ServiceFormSuccess title="Quote Request Sent" message={successMessage || "Thank you for your turfing quote request! We'll contact you shortly with a free estimate for your garden project."} onReset={resetForm} resetLabel="Request Another Quote" />;
  }
  return <Form onSubmit={handleSubmit} className={formCardClass}>
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Full Name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" className={inputClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className={inputClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required",
      pattern: {
        value: /^[0-9+\-\s()]*$/,
        message: "Please enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="07443 802652" className={inputClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="postcode" rules={{
      required: "Postcode is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Postcode</FormLabel>
            <FormControl>
              <Input placeholder="e.g. SO15 1AA" className={inputClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_type" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Service Required</FormLabel>
            <FormControl>
              <select {...field} className={selectClass}>
                <option value="">Select service...</option>
                <option value="Artificial Grass">Artificial Grass</option>
                <option value="Natural Turf">Natural Turf</option>
                <option value="Drainage">Drainage</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Garden Design">Garden Design</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="property_type" rules={{
      required: "Please select a property type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Property Type</FormLabel>
            <FormControl>
              <select {...field} className={selectClass}>
                <option value="">Select property type...</option>
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
                <option value="Detached House">Detached House</option>
                <option value="Semi-Detached House">Semi-Detached House</option>
                <option value="Bungalow">Bungalow</option>
                <option value="Other">Other</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_description" rules={{
      required: "Project details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Project Details</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Tell us about your garden project..." className={textareaClass} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className={submitClass}>
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : "Get My Free Quote"}
      </Button>
    </Form>;
}
export default function TurfingQuoteSection() {
  return <ServiceQuoteSection id="get-your-free-quote" eyebrow="GET A FREE QUOTE" heading="Ready for a Pristine Green Lawn?" intro="Invest in a lawn that lasts. Our premium turfing and artificial grass solutions combine exceptional durability with timeless aesthetic appeal, tailored to enhance your garden across Southampton, Hampshire & surrounding areas." serviceName="Turfing & Artificial Grass">
      <FormProvider formId="start_project_form_get_in_touch" sectionName="Get A Free Quote" defaultValues={{
      full_name: "",
      email: "",
      phone: "",
      postcode: "",
      service_type: "",
      property_type: "",
      project_description: ""
    }} validationRules={{
      full_name: {
        required: true
      },
      email: {
        required: true,
        pattern: "email"
      },
      phone: {
        required: true
      },
      project_description: {
        required: true
      }
    }} submitText="Get My Free Quote" successMessage="Thank you for your turfing quote request! We'll contact you shortly with a free estimate for your garden project in Southampton.">
        <TurfingQuoteForm />
      </FormProvider>
    </ServiceQuoteSection>;
}